/**
 * AuthorCell — `.author` from the prototype (cms-redesign-mockup.html
 * ~line 2391–2402). 26×26 circular avatar (image or initials) + author
 * name. Used in the Sessions table's Author column and any other place
 * a single author is referenced inline.
 *
 *   <AuthorCell name="Nick Ortner" imageUrl="https://…/nick.jpg" />
 *   <AuthorCell name="Jessica Ortner" />
 */
import React from 'react';
import { cn } from './cn';

/** Avatar tint palette — picked deterministically from the initials so the
 *  same author always shows the same color. Matches the prototype's
 *  `.author .ai.a-{n|j|a|k}` variants. */
const TONE = [
  'rgb(var(--color-navy))', // a-n  — navy
  '#C4715B', // a-j  — terracotta
  '#6B6776', // a-a  — grey
  'rgb(var(--color-teal-green))', // a-k  — teal-green
];

function pickTone(seed) {
  if (!seed) return TONE[0];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return TONE[h % TONE.length];
}

export function AuthorCell({ name, imageUrl, className }) {
  const initials = (name || '')
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const tone = pickTone(initials);
  return (
    // `.author` — inline-flex, gap 9, navy 13px font-weight 500.
    <span
      className={cn('inline-flex items-center min-w-0', className)}
      style={{ gap: 9, fontSize: 13, fontWeight: 500, color: 'rgb(var(--color-navy))' }}
    >
      {/* `.ai` — 26×26 circle, Asap Condensed 10.5/700 white. */}
      <span
        className="grid place-items-center shrink-0 overflow-hidden font-condensed font-bold text-white"
        style={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          fontSize: 10.5,
          background: imageUrl ? undefined : tone,
          backgroundImage: imageUrl ? `url('${imageUrl}')` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {!imageUrl && initials}
      </span>
      <span className="truncate min-w-0">{name || '—'}</span>
    </span>
  );
}

export default AuthorCell;
